import {
  CreateButton,
  DeleteButton,
  ExportButton,
  List,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Alert,
  Typography,
  Timeline,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useModal } from "@refinedev/core";

import { IEnpData } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";

export const EnpDataList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<IEnpData>();

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IEnpData>({
      resource: "enp-data",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { title, enpNumber } = params;

        filters.push({
          field: "title",
          operator: "contains",
          value: title ? title : undefined,
        });
        filters.push({
          field: "enpNumber",
          operator: "contains",
          value: enpNumber ? enpNumber : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const columns: ColumnsType<IEnpData> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 300,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "ENP Number",
      dataIndex: "enpNumber",
      key: "enpNumber",
      width: 140,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("enpNumber", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={prettyBytes(value)} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Icons.DownloadOutlined />}
            href={record?.storageUrl}
            download={true}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            disabled
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "e-NP Data",
          subTitle: "e-nautical publications",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <CreateButton hidden />
                <ExportButton hidden />
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} lg={24} xs={24}>
          <Alert
              type="info"
              message={
                <Typography.Title level={5} style={{ color: 'inherit', margin: '0', padding: '0' }}>
                  How to use e-NP Publications:
                </Typography.Title>
              }
              description={
                <div style={{ padding: '10px 0' }}>
                  <Timeline style={{ paddingBottom: 0 }}>
                    <Timeline.Item style={{ paddingTop: 9 }}>
                      <Typography.Text strong>Step 1</Typography.Text>{" "}
                      Create a "Publications" folder
                    </Timeline.Item>
                    <Timeline.Item>
                      <Typography.Text strong>Step 2</Typography.Text>{" "}
                      Download and copy only the new edition e-NP PDF files into this folder
                    </Timeline.Item>
                    <Timeline.Item>
                      <Typography.Text strong>Step 3</Typography.Text>{" "}
                      Transfer the "Publications" folder to a USB drive or another local drive (like D: or E:)
                    </Timeline.Item>
                    <Timeline.Item style={{ paddingBottom: 0 }}>
                      <Typography.Text strong>Step 4</Typography.Text>{" "}
                      Load the e-NP data onto your e-Reader from the transfer location
                    </Timeline.Item>
                  </Timeline>
                </div>
              }
              showIcon
              style={{
                marginBottom: '16px',
                paddingBottom: '0px',
              }}
            />
            <Card type="inner" title={"Filter"}>
              <Filter formProps={searchFormProps} filters={filters || []} />
            </Card>
          </Col>
          <Col xl={24} xs={24}>
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
      </List>
      <Modal
        centered
        closeIcon={<Icons.CloseSquareFilled />}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={null}
      >
        <div>
          <embed
            src={record?.embedUrl}
            type="application/pdf"
            width="99%"
            height="700px"
          />
        </div>
      </Modal>
    </>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        title: getDefaultFilter("title", filters),
        edpNumber: getDefaultFilter("edpNumber", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={10}>
          <Form.Item label={"Title"} name="title">
            <Input placeholder="filter by title" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={10}>
          <Form.Item label={"ENP Number"} name="enpNumber">
            <Input placeholder="filter by ENP number" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={4}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
